/**
 * useQueryList — the canonical TanStack React Query read hook.
 * ---------------------------------------------------------------------------
 * This is the React-Query equivalent of `useSupabaseList` from
 * `../useSupabaseCrud.ts`. It runs the SAME Supabase `select` + filter +
 * pagination logic, but the loading / data / error state is owned by React
 * Query's cache instead of local `useState`. That buys us, for free:
 *
 *   - Caching across navigation: revisit a screen within `staleTime` (30s, set
 *     in `services/queryClient.ts`) and the data paints instantly from cache
 *     instead of re-hitting Supabase.
 *   - Deduplication: two components asking for the same `queryKey` share one
 *     in-flight request.
 *   - Declarative invalidation: a mutation can call
 *     `queryClient.invalidateQueries({ queryKey: [...] })` to refetch any
 *     screen showing that data, instead of threading manual `refetch()` calls.
 *
 * Adoption is INCREMENTAL and additive: existing `useSupabaseList` hooks keep
 * working untouched. Migrate a hook to this one screen at a time — see
 * `./README.md` for the step-by-step.
 *
 * Return shape is intentionally close to `useSupabaseList` so a list call-site
 * can swap with minimal churn:
 *   { response, processing, errors, totalCount, page, pageCount, goToPage,
 *     setPage, refetch, isError }
 */
import { useState } from 'react';
import {
  useQuery,
  useQueryClient,
  keepPreviousData,
  type QueryKey,
} from '@tanstack/react-query';
import { supabase, type Tables, type Database } from '../supabase';
import { reportError } from '../reportError';
import { getErrorMessage } from '@utils/helper';

type TableName = keyof Database['public']['Tables'];

export interface QueryListOptions<T extends TableName> {
  /** Supabase table name. */
  table: T;
  /** Columns to select (PostgREST select syntax). Default '*'. */
  select?: string;
  /**
   * Stable base of the React Query cache key. The hook appends the current
   * `params` and `page` automatically, so two screens reading the same table
   * with different filters don't collide. Make this unique + descriptive,
   * e.g. ['grade_levels', 'list'].
   */
  queryKey: QueryKey;
  /**
   * Additional filters applied to every query (e.g. org_id scoping). Mirrors
   * `useSupabaseList.baseFilters` — receives the PostgREST builder + the
   * current params object. The builder generics are internal to
   * @supabase/postgrest-js (not exported) so it is typed loosely, exactly as
   * in useSupabaseCrud; chain `.eq` / `.ilike` / `.or` / `.in` and return it.
   */
  baseFilters?: (query: any, params: Record<string, any>) => any;
  /** Param keys that should NOT be auto-applied as `.eq(key, value)`. */
  excludeFromAutoFilter?: string[];
  /** Page size. Default 25. */
  pageSize?: number;
  /** Order by column. Default 'created_at'. */
  orderBy?: string & keyof Tables<T>;
  /** Ascending order? Default false (newest first). */
  ascending?: boolean;
  /**
   * When false, the query is disabled (won't fetch). Use for dependent reads
   * that need an id/param before they can run. Default true.
   */
  enabled?: boolean;
  staleTime?: number;
}

/** Keys that never map to a real column (pagination / view-state). */
const NON_COLUMN_KEYS = new Set(['next_page_token', 'page', 'per_page']);

export function useQueryList<
  T extends TableName,
  TParams extends Record<string, any> = Record<string, any>,
  // Row shape returned by the query. Defaults to the raw table Row, but callers
  // whose `select` string embeds related rows (PostgREST joins) can pass an
  // embed-aware type so `response` reflects the joined keys. Type-only — the
  // runtime query (which already fetches the embeds) is unchanged.
  TRow = Tables<T>,
>(params: TParams, options: QueryListOptions<T>) {
  const {
    table,
    select = '*',
    queryKey,
    baseFilters,
    excludeFromAutoFilter,
    pageSize = 25,
    orderBy = 'created_at' as string & keyof Tables<T>,
    ascending = false,
    enabled = true,
    staleTime,
  } = options;

  const excludedKeys = new Set(excludeFromAutoFilter ?? []);

  // Offset pagination state lives locally; the page index is folded into the
  // query key so each page is cached independently.
  const [page, setPage] = useState(0);
  const client = useQueryClient();
  const clientStaleTime = client.getDefaultOptions().queries?.staleTime;

  const query = useQuery({
    // params + page are part of the key so changing a filter or page produces a
    // distinct cache entry and triggers a refetch.
    queryKey: [...queryKey, params, page],
    enabled,
    staleTime: staleTime ?? (typeof clientStaleTime === 'number' ? clientStaleTime : undefined),
    // While a NEW page/filter loads, keep showing the previous page's rows so
    // the table doesn't flash empty — matches the legacy hook's feel.
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const from = page * pageSize;
      const to = from + pageSize - 1;

      let q = supabase
        .from(table)
        .select(select, { count: 'exact' })
        .range(from, to)
        .order(orderBy, { ascending });

      if (orderBy !== 'id') q = q.order('id', { ascending: true });

      if (baseFilters) {
        q = baseFilters(q as any, params) as any;
      }

      for (const [key, value] of Object.entries(params)) {
        if (value === undefined || value === null || value === '') continue;
        if (NON_COLUMN_KEYS.has(key)) continue;
        if (excludedKeys.has(key)) continue;
        if (Array.isArray(value)) {
          if (value.length === 0) continue;
          q = (q as any).in(key, value);
        } else {
          q = (q as any).eq(key, value);
        }
      }

      const { data: rows, error, count } = await q;

      if (error) {
        // Surface through the same reporter as useSupabaseCrud, then throw so
        // React Query routes it into `error` / `isError`.
        const msg = reportError({
          op: `Load ${table}`,
          err: error,
          silent: true,
          tags: { table: String(table), verb: 'list' },
        });
        throw new Error(msg);
      }

      return {
        rows: (rows as unknown as TRow[]) ?? [],
        count: count ?? 0,
      };
    },
  });

  const totalCount = query.data?.count ?? 0;
  const pageCount = Math.max(1, Math.ceil((totalCount || 0) / pageSize));

  const goToPage = (target: number) => {
    const clamped = Math.min(Math.max(0, target), Math.max(0, pageCount - 1));
    setPage(clamped);
  };

  return {
    /** Rows from the last successful fetch (null until first load, like the legacy hook). */
    response: query.data?.rows ?? null,
    /** Exact total row count from PostgREST. */
    totalCount,
    /** In-flight indicator. Mirrors `processing` from useSupabaseList. */
    processing: query.isPending || query.isFetching,
    /** Error map keyed like the legacy hook ({ general } when present). */
    errors: query.isError
      ? { general: getErrorMessage(query.error, 'Request failed') }
      : {},
    /** Raw React Query error flag, for callers that want it. */
    isError: query.isError,
    page,
    setPage,
    pageCount,
    goToPage,
    /** Force a refetch (e.g. after a mutation, if not using invalidateQueries). */
    refetch: query.refetch,
    /** Escape hatch to the full React Query result if a caller needs more. */
    query,
  };
}
